import { chevronIcon, folderIcon, mapIcon, moreIcon, searchIcon, syncIcon } from "../icons.js";
import { formatShortDate } from "../utils/dates.js";
import { escapeAttr, escapeHtml } from "../utils/html.js";
import { renderEmpty } from "./empty.js";
import { t } from "../../../../shared/i18n/index.js";

// Share texts reused from the share area (same English as before).
const ROLES = ["owner", "admin", "editor", "viewer"];

/** A share role inside a sentence ("editor"); unknown roles as they come. */
function roleName(role) {
  return ROLES.includes(role) ? t(`share.roleInText.${role}`) : role;
}

/** "From Ann · editor" under a pending invitation. */
export function invitationMeta(from, role) {
  return `${t("share.inbox.from", { name: from })} · ${roleName(role)}`;
}

export function renderDocs(appState, deps) {
  const {
    callbacks,
    uiState,
    buildFolderTrail,
    docMatchesQuery,
    getActiveDocsFolderId,
    getRootFolderId,
    isOwnerDoc,
    normalizeFolderId,
  } = deps;
  const index = callbacks.VaultStorage?.getIndex?.();
  const folders = index?.folders || [];
  const rootFolderId = getRootFolderId(index);
  const activeFolderId = getActiveDocsFolderId(index);
  const query = uiState.docsSearchQuery.trim().toLowerCase();
  const allDocs = (index?.docs || [])
    .slice()
    .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
  if (!allDocs.length && appState.currentDocId && appState.data) {
    allDocs.push({
      docId: appState.currentDocId,
      title: appState.data.name || t("mobile.docs.currentDocument"),
      updatedAt: new Date().toISOString(),
      role: appState.shareRole || "owner",
    });
  }
  const folderDocs = query
    ? allDocs.filter((doc) => docMatchesQuery(doc, query))
    : allDocs.filter((doc) => isOwnerDoc(doc) && normalizeFolderId(doc.folderId, rootFolderId) === activeFolderId);
  const childFolders = query
    ? []
    : folders
        .filter((folder) => folder.parentId === activeFolderId)
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const sharedDocs = query
    ? []
    : allDocs.filter((doc) => !isOwnerDoc(doc));
  const currentFolder = folders.find((folder) => folder.id === activeFolderId);
  const canNavigateUp = !!currentFolder && currentFolder.parentId !== null;

  const folderTrail = buildFolderTrail(folders, activeFolderId);
  return `
    <section class="mobile-view mobile-view-padded" data-mobile-pull-refresh>
      <div class="mobile-pull-indicator" data-mobile-pull-indicator>${syncIcon()}</div>
      <div class="mobile-vault-heading"><h1>${escapeHtml(t("html.vault.title"))}</h1><span>${escapeHtml(t("mobile.docs.mapCount", { count: allDocs.length }))}</span></div>
      ${renderInvitations(callbacks)}
      <div class="mobile-storage-summary" data-storage-summary></div>
      <label class="mobile-search-field">
        <span>${searchIcon()}</span>
        <input value="${escapeAttr(uiState.docsSearchQuery)}"
               data-mobile-action="docs-search"
               placeholder="${escapeAttr(t("mobile.docs.searchPlaceholder"))}" aria-label="${escapeAttr(t("mobile.docs.searchAria"))}" />
      </label>
      ${folders.filter(f => f.parentId === null).length > 1 ? `<div class="mobile-segmented">${folders.filter(f => f.parentId === null).map(folder => `<button class="${folderTrail[0]?.id === folder.id ? 'active' : ''}" data-mobile-action="open-folder" data-folder-id="${escapeAttr(folder.id)}">${escapeHtml(folder._storage === 'local' ? t("vault.details.onDevice") : t("vault.details.onlineVault"))}</button>`).join('')}</div>` : ''}
      <nav class="mobile-folder-breadcrumbs" aria-label="${escapeAttr(t("mobile.docs.folderPath"))}">
        ${folderTrail.map((entry, idx) => `
          <button class="mobile-crumb ${idx === folderTrail.length - 1 ? "current" : ""}"
                  data-mobile-action="open-folder"
                  data-folder-id="${escapeAttr(entry.id || "")}">${escapeHtml(entry.name)}</button>
          ${idx < folderTrail.length - 1 ? `<span class="mobile-crumb-sep">${chevronIcon()}</span>` : ""}
        `).join("")}
      </nav>
      <div class="mobile-docs-toolbar">
        ${canNavigateUp ? `<button class="mobile-secondary-btn compact" data-mobile-action="up-folder">${escapeHtml(t("mobile.docs.up"))}</button>` : ""}
        <div class="mobile-section-title">${escapeHtml(query ? t("mobile.docs.searchResults") : currentFolder?.name || t("mobile.docs.documents"))}</div>
        <button class="mobile-secondary-btn compact" data-mobile-action="create-folder">${escapeHtml(t("vault.folder.new"))}</button>
        <button class="mobile-primary-btn compact" data-mobile-action="create-doc">${escapeHtml(t("vault.map.new"))}</button>
      </div>
      ${childFolders.length ? `
        <div class="mobile-doc-list">
          ${childFolders.map(renderFolderRow).join("")}
        </div>
      ` : ""}
      <div class="mobile-doc-list">
        ${folderDocs.length ? folderDocs.map((doc) => renderDocRow(doc, appState.currentDocId)).join("") : renderEmpty(query ? t("vault.picker.noMatches") : t("mobile.docs.empty"))}
      </div>
      ${sharedDocs.length ? `
        <div class="mobile-section-title">${escapeHtml(t("mobile.docs.sharedWithMe"))}</div>
        <div class="mobile-doc-list">
          ${sharedDocs.map((doc) => renderDocRow(doc, appState.currentDocId)).join("")}
        </div>
      ` : ""}
      <div class="mobile-vault-footer"><button class="mobile-secondary-btn compact" data-mobile-action="vault-import">${escapeHtml(t("vault.data.import"))}</button><button class="mobile-secondary-btn compact" data-mobile-action="vault-export">${escapeHtml(t("vault.data.export"))}</button></div>
    </section>
  `;
}

/**
 * Pending share invitations.
 *
 * The notifications module keeps this list live over the user-WS for both
 * shells, but the accept/decline UI used to exist only in the desktop header
 * (which mobile.css hides), so a mobile-only user who was invited to a doc had
 * no way to accept it. Rendered above the search field because an invitation
 * is time-sensitive and must not sit below the fold.
 */
function renderInvitations(callbacks) {
  let invitations = [];
  try {
    invitations = callbacks.getInvitations?.() || [];
  } catch (err) {
    console.warn("[Mobile] getInvitations failed:", err);
    return "";
  }
  if (!invitations.length) return "";

  return `
    <div class="mobile-invitations" role="region" aria-label="${escapeAttr(t("mobile.invite.pendingAria"))}">
      <div class="mobile-section-title">
        ${escapeHtml(t("share.inbox.invitations"))}
        <span class="mobile-invite-count">${invitations.length}</span>
      </div>
      <div class="mobile-doc-list">
        ${invitations.map((inv) => {
          const title = inv.share_title || t("mobile.invite.untitled");
          const from = inv.inviter_username || t("mobile.invite.someone");
          const role = inv.role || "viewer";
          const id = escapeAttr(inv.id || "");
          return `
            <div class="mobile-doc-row mobile-invite-row">
              <span class="mobile-doc-main">
                <span class="mobile-doc-title">${escapeHtml(title)}</span>
                <span class="mobile-doc-meta">${escapeHtml(invitationMeta(from, role))}</span>
              </span>
              <span class="mobile-invite-actions">
                <button class="mobile-secondary-btn compact"
                        data-mobile-action="ignore-inviter"
                        data-invitation-id="${id}"
                        data-inviter="${escapeAttr(from)}">${escapeHtml(t("share.inbox.ignore"))}</button>
                <button class="mobile-secondary-btn compact"
                        data-mobile-action="decline-invitation"
                        data-invitation-id="${id}">${escapeHtml(t("share.inbox.decline"))}</button>
                <button class="mobile-primary-btn compact"
                        data-mobile-action="accept-invitation"
                        data-invitation-id="${id}">${escapeHtml(t("share.inbox.accept"))}</button>
              </span>
            </div>
          `;
        }).join("")}
      </div>
    </div>
  `;
}

function renderFolderRow(folder) {
  return `
    <div class="mobile-doc-row mobile-folder-row">
      <button class="mobile-doc-open"
              data-mobile-action="open-folder"
              data-folder-id="${escapeAttr(folder.id)}">
        <span class="mobile-folder-dot">${folderIcon()}</span>
        <span class="mobile-doc-main">
          <span class="mobile-doc-title">${escapeHtml(folder.name || t("common.folder"))}</span>
        </span>
        <span class="mobile-chevron">${chevronIcon()}</span>
      </button>
      <button class="mobile-row-action"
              data-mobile-action="open-folder-actions"
              data-folder-id="${escapeAttr(folder.id)}"
              aria-label="${escapeAttr(t("vault.folder.actions"))}">${moreIcon()}</button>
    </div>
  `;
}

function renderDocRow(doc, currentDocId) {
  const title = doc.title || doc.name || t("common.untitled");
  const date = formatShortDate(doc.updatedAt || doc.createdAt);
  const role = doc.role && doc.role !== "owner" ? roleName(doc.role) : "";
  const active = doc.docId === currentDocId;
  return `
    <div class="mobile-doc-row ${active ? "active" : ""}">
      <button class="mobile-doc-open"
              data-mobile-action="open-doc"
              data-doc-id="${escapeAttr(doc.docId)}">
        <span class="mobile-map-ico">${mapIcon()}</span>
        <span class="mobile-doc-main">
          <span class="mobile-doc-title">${escapeHtml(title)}</span>
          <span class="mobile-doc-size" data-storage-doc-id="${escapeAttr(doc.docId)}">${escapeHtml(t("vault.size.loading"))}</span>
          <span class="mobile-doc-meta">${[date, role].filter(Boolean).map(escapeHtml).join(" &middot; ")}</span>
        </span>
        <span class="mobile-chevron">${chevronIcon()}</span>
      </button>
      <button class="mobile-row-action"
              data-mobile-action="open-doc-actions"
              data-doc-id="${escapeAttr(doc.docId)}"
              aria-label="${escapeAttr(t("mobile.docs.docActions"))}">${moreIcon()}</button>
    </div>
  `;
}
